import React, { useState, useEffect } from "react";
import { Download } from "react-feather";
import { Table, Button, Dropdown, Menu, Switch, Popover, message } from "antd";
import { Link } from "react-router-dom";
import { PhoneOutlined, FilterOutlined, DownOutlined } from "@ant-design/icons";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import DatePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import exportService from "../../utils/exportService";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import PrintReportPreview from "../../reports/PrintReportPreview";
import { renderDate } from "../../utils/dateFormatter"; // Assuming this utility exists

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";

const OTABookingVolumeReport = () => {
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const today = moment().startOf("day");
    const thirtyDaysAgo = moment().subtract(29, "days").startOf("day");
    return {
      startDate: thirtyDaysAgo,
      endDate: today,
    };
  });
  const [businessSourceFilter, setBusinessSourceFilter] = useState(null);
  const [bookingStatusFilter, setBookingStatusFilter] = useState(null);
  const [roomTypeFilter, setRoomTypeFilter] = useState(null);
  const [showGraph, setShowGraph] = useState(true);
  const [pdfVisible, setPdfVisible] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [dateRange]);

  const fetchReport = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");

    if (!dateRange.startDate || !dateRange.endDate || !moment(dateRange.startDate).isValid() || !moment(dateRange.endDate).isValid()) {
      Swal.fire({
        title: t("error"),
        text: t("invalid_date_range"),
        icon: "error",
      });
      setData([]);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${apiURL}/reports-daily/ota-booking-volume`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: dateRange.startDate.format("YYYY-MM-DD"),
          endDate: dateRange.endDate.format("YYYY-MM-DD"),
        },
      });
      if (response.data.success) {
        setData(response.data.otaBookings || []);
      } else {
        Swal.fire({
          title: t("error"),
          text: t("failed_fetch_ota_booking_volume_report"),
          icon: "error",
        });
        setData([]);
      }
    } catch (error) {
      Swal.fire({
        title: t("error"),
        text: t("failed_fetch_ota_booking_volume_report"),
        icon: "error",
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter((booking) => {
    const matchesBusinessSource = businessSourceFilter ? booking.business_source_name === businessSourceFilter.value : true;
    const matchesBookingStatus = bookingStatusFilter ? booking.booking_status === bookingStatusFilter.value : true;
    const matchesRoomType = roomTypeFilter ? booking.room_type === roomTypeFilter.value : true;
    return matchesBusinessSource && matchesBookingStatus && matchesRoomType;
  });

  const totalOtaBookings = filteredData.length;
  const uniqueBusinessSources = [...new Set(filteredData.map((booking) => booking.business_source_name || "Unknown"))];
  const averageBookingsPerSource = uniqueBusinessSources.length > 0 ? totalOtaBookings / uniqueBusinessSources.length : 0;
  const totalCanceledBookings = filteredData.filter((booking) => booking.booking_status === "cancelled").length;
  const cancellationRate = totalOtaBookings > 0 ? (totalCanceledBookings / totalOtaBookings) * 100 : 0;

  const activityStats = [
    { label: t("total_ota_bookings"), value: `${totalOtaBookings}`, color: "#28a745" },
    { label: t("average_bookings_per_ota"), value: `${averageBookingsPerSource.toFixed(1)}`, color: "#007bff" },
    { label: t("cancellation_rate"), value: `${cancellationRate.toFixed(1)}%`, color: "#dc3545" },
  ];

  const barChartData = {
    labels: uniqueBusinessSources,
    datasets: [
      {
        label: "Booking Volume",
        data: uniqueBusinessSources.map((source) => filteredData.filter((booking) => booking.business_source_name === source).length),
        backgroundColor: ["#007bff", "#28a745", "#dc3545", "#ffc107", "#6f42c1"],
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Number of Bookings" },
      },
    },
  };

  const handleDateRangeChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (param === "today") {
          newRange = { startDate: moment().startOf("day"), endDate: moment().endOf("day") };
        } else if (param === "yesterday") {
          newRange = { startDate: moment().subtract(1, "days").startOf("day"), endDate: moment().subtract(1, "days").endOf("day") };
        } else if (param === "last7Days") {
          newRange = { startDate: moment().subtract(7, "days").startOf("day"), endDate: moment().endOf("day") };
        } else if (param === "last30Days") {
          newRange = { startDate: moment().subtract(30, "days").startOf("day"), endDate: moment().endOf("day") };
        } else if (param === "thisMonth") {
          newRange = { startDate: moment().startOf("month").startOf("day"), endDate: moment().endOf("month").endOf("day") };
        } else if (param === "lastMonth") {
          newRange = { startDate: moment().subtract(1, "month").startOf("month").startOf("day"), endDate: moment().subtract(1, "month").endOf("month").endOf("day") };
        } else if (param === "customRange") {
          return prevRange; // Custom range handled by onApply callback
        }
      } else {
        const { start, end } = param;
        newRange = {
          startDate: moment(start).startOf("day"),
          endDate: moment(end).startOf("day"),
        };
      }

      if (!newRange?.startDate || !newRange?.endDate || !newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        Swal.fire({
          title: t("error"),
          text: t("invalid_date_selected"),
          icon: "error",
        });
        setData([]);
        return prevRange;
      }

      if (newRange.startDate.isAfter(newRange.endDate)) {
        Swal.fire({
          title: t("error"),
          text: t("start_date_after_end_date"),
          icon: "error",
        });
        setData([]);
        return prevRange;
      }

      fetchReport();
      return newRange;
    });
  };

  const handleBusinessSourceFilterChange = (option) => {
    setBusinessSourceFilter(option);
  };

  const handleBookingStatusFilterChange = (option) => {
    setBookingStatusFilter(option);
  };

  const handleRoomTypeFilterChange = (option) => {
    setRoomTypeFilter(option);
  };

  const exportHeaders = [
    { key: "check_in_date", title: t("date") },
    { key: "booking_number", title: t("booking_number") },
    { key: "business_source_name", title: t("ota_platform") },
    { key: "room_type", title: t("room_type") },
    { key: "booking_status", title: t("status") },
    { key: "guest_name", title: t("guest_name") },
    { key: "check_in_date", title: t("check_in") },
    { key: "check_out_date", title: t("check_out") },
  ];

  const exportData = filteredData.map((booking) => ({
    check_in_date: renderDate(booking.check_in_date) || "-",
    booking_number: booking.booking_number || "-",
    business_source_name: booking.business_source_name || "Unknown",
    room_type: booking.room_type || "-",
    booking_status: booking.booking_status.replace("_", " ").toUpperCase(),
    guest_name: booking.guest_name || "-",
    check_in_date: renderDate(booking.check_in_date) || "-",
    check_out_date: renderDate(booking.check_out_date) || "-",
  }));

  // Calculate totals for export footer
  const totalReservations = filteredData.length;
  const totalNights = filteredData.reduce(
    (sum, booking) => sum + moment(booking.check_out_date).diff(moment(booking.check_in_date), "days"),
    0
  );
  const totalDirectBookings = filteredData.filter(
    (booking) => booking.business_source_name === "Direct" || !booking.business_source_name
  ).length;

  const totalsForPageFooter = [
    { key: "Total Reservations", value: totalReservations },
    { key: "Total Nights", value: totalNights },
    { key: "Direct Bookings", value: totalDirectBookings },
  ];

  const exportFooter = [
    { key: "Total Reservations", value: totalReservations },
    { key: "Total Nights", value: totalNights },
    { key: "Direct Bookings", value: totalDirectBookings },
  ];

  const suffix = `_${moment().format("YYYYMMDD")}`;

  const handleExportXLS = () => {
    exportService.exportToXLS(
      exportData,
      exportHeaders,
      `ota_booking_volume_report${suffix}.xlsx`,
      "OTA Booking Volume Report"
    );
  };

  const handleExportCSV = () => {
    exportService.exportToCSV(
      exportData,
      exportHeaders,
      `ota_booking_volume_report${suffix}.csv`
    );
  };

  const handleExportPDF = () => {
    exportService.exportToPDF(
      exportData,
      exportHeaders,
      "OTA Booking Volume Report",
      `ota_booking_volume_report${suffix}.pdf`,
      false,
      true,
      totalsForPageFooter,
      exportFooter
    );
  };

  const handlePrint = async () => {
    try {
      setLoading(true);
      const pdfBlob = await exportService.exportToPDF(
        exportData,
        exportHeaders,
        "OTA Booking Volume Report",
        null,
        true,
        true,
        totalsForPageFooter,
        exportFooter
      );
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfData(pdfUrl);
      setPdfVisible(true);
    } catch (error) {
      console.error("Error generating PDF for preview:", error);
      message.error("Failed to load PDF preview.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setPdfVisible(false);
    setPdfData(null);
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={handleExportCSV}>
        Download CSV
      </Menu.Item>
      <Menu.Item key="xls" onClick={handleExportXLS}>
        Download XLS
      </Menu.Item>
      <Menu.Item key="pdf" onClick={handleExportPDF}>
        Download PDF
      </Menu.Item>
      <Menu.Item key="print" onClick={handlePrint}>
        Print Preview
      </Menu.Item>
    </Menu>
  );

  const businessSourceOptions = [
    { value: "Booking.com", label: "Booking.com" },
    { value: "Expedia", label: "Expedia" },
    { value: "Agoda", label: "Agoda" },
    { value: "MakeMyTrip", label: "MakeMyTrip" },
  ];

  const bookingStatusOptions = [
    { value: "checked_in", label: t("confirmed") },
    { value: "cancelled", label: t("canceled") },
    { value: "reserved", label: t("tentative") },
    { value: "checked_out", label: t("checked_out") },
  ];

  const roomTypeOptions = [
    { value: "Deluxe Room", label: "Deluxe Room" },
    { value: "Suite Room", label: "Suite Room" },
    { value: "Standard Room", label: "Standard Room" },
    { value: "Family Room", label: "Family Room" },
    { value: "Executive Suite", label: "Executive Suite" },
    { value: "Premium Suite", label: "Premium Suite" },
    { value: "Twin Room", label: "Twin Room" },
    { value: "Single Room", label: "Single Room" },
  ];

  const filterPopupContent = (
    <div className="p-4" style={{ width: "400px" }}>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("filter_by_business_source")}</label>
        <Select
          value={businessSourceFilter}
          onChange={handleBusinessSourceFilterChange}
          options={businessSourceOptions}
          isClearable
          placeholder={t("filter_by_business_source")}
          styles={{
            container: (provided) => ({ ...provided, width: "100%" }),
            control: (provided) => ({
              ...provided,
              minHeight: "32px",
              borderRadius: "4px",
              borderColor: "#d9d9d9",
              background: "#fff",
            }),
            menu: (provided) => ({ ...provided, width: "100%" }),
          }}
        />
      </div>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("filter_by_booking_status")}</label>
        <Select
          value={bookingStatusFilter}
          onChange={handleBookingStatusFilterChange}
          options={bookingStatusOptions}
          isClearable
          placeholder={t("filter_by_booking_status")}
          styles={{
            container: (provided) => ({ ...provided, width: "100%" }),
            control: (provided) => ({
              ...provided,
              minHeight: "32px",
              borderRadius: "4px",
              borderColor: "#d9d9d9",
              background: "#fff",
            }),
            menu: (provided) => ({ ...provided, width: "100%" }),
          }}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("filter_by_room_type")}</label>
        <Select
          value={roomTypeFilter}
          onChange={handleRoomTypeFilterChange}
          options={roomTypeOptions}
          isClearable
          placeholder={t("filter_by_room_type")}
          styles={{
            container: (provided) => ({ ...provided, width: "100%" }),
            control: (provided) => ({
              ...provided,
              minHeight: "32px",
              borderRadius: "4px",
              borderColor: "#d9d9d9",
              background: "#fff",
            }),
            menu: (provided) => ({ ...provided, width: "100%" }),
          }}
        />
      </div>
    </div>
  );

  const MIN_ROWS = 10;
  const bookingData = filteredData.map((booking, index) => ({
    key: `booking-${index}`,
    check_in: booking.check_in_date,
    check_out: booking.check_out_date,
    booking_number: booking.booking_number,
    business_source_name: booking.business_source_name || "Unknown",
    room_type: booking.room_type || "-",
    booking_status: booking.booking_status,
    guest_name: booking.guest_name || "-",
  }));

  const maxRows = Math.max(bookingData.length, MIN_ROWS);
  const balancedBookingData = [...bookingData];

  for (let i = bookingData.length; i < maxRows; i++) {
    balancedBookingData.push({
      key: `empty-booking-${i}`,
      check_in: "",
      check_out: "",
      booking_number: "",
      business_source_name: "",
      room_type: "",
      booking_status: "",
      guest_name: "",
    });
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-2">
              <h3 className="page-title">OTA Booking Volume</h3>
            </div>

            <div className="col-md-10">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Left Side: Date Navigation */}
                <div className="d-flex align-items-center">
                  <DatePicker
                    initialSettings={{
                      singleDatePicker: false,
                      showDropdowns: true,
                      startDate: dateRange.startDate.toDate(),
                      endDate: dateRange.endDate.toDate(),
                      ranges: {
                        Today: [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
                        Yesterday: [moment().subtract(1, "days").startOf("day").toDate(), moment().subtract(1, "days").endOf("day").toDate()],
                        "Last 7 Days": [moment().subtract(7, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
                        "Last 30 Days": [moment().subtract(30, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
                        "This Month": [moment().startOf("month").startOf("day").toDate(), moment().endOf("month").endOf("day").toDate()],
                        "Last Month": [moment().subtract(1, "month").startOf("month").startOf("day").toDate(), moment().subtract(1, "month").endOf("month").endOf("day").toDate()],
                      },
                      locale: {
                        format: "DD MMM YYYY",
                        applyLabel: "Select",
                        cancelLabel: "Cancel",
                      },
                    }}
                    onApply={(event, picker) => {
                      handleDateRangeChange({ start: picker.startDate, end: picker.endDate });
                    }}
                  >
                    <div className="input-group" style={{ width: "250px" }}>
                      <input
                        type="text"
                        className="form-control text-center fw-bold"
                        value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                        style={{ height: "38px", fontSize: "15px", borderRight: "none", backgroundColor: "#ffffff" }}
                        readOnly
                      />
                      <div className="input-group-append">
                        <span
                          className="input-group-text"
                          style={{
                            backgroundColor: "#fff",
                            borderLeft: "none",
                            borderRadius: "0 4px 4px 0",
                            cursor: "pointer",
                            height: "38px",
                          }}
                        >
                          <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
                        </span>
                      </div>
                    </div>
                  </DatePicker>
                </div>

                {/* Center: Search (Optional, not in original but added for consistency) */}
                <div style={{ flexGrow: 1, maxWidth: 400 }}>
                  {/* Uncomment if search is desired */}
                  {/* <Search
                    placeholder="Search by Name, Email, or Booking ID"
                    allowClear
                    enterButton="Search"
                    size="large"
                    onChange={(e) => setSearchText(e.target.value)}
                  /> */}
                </div>

                {/* Right Side: Icons and Add Button */}
                <div className="d-flex align-items-center flex-shrink-0 gap-2">
                  <div className="view-icons d-flex align-items-center gap-1">
                    <Link to="#" className="btn btn-link" data-bs-toggle="modal" data-bs-target="#scrollable-modal">
                      <i className="las la-filter" />
                    </Link>
                    {/* Maximize button can be added if needed, following ReservationList pattern */}
                    {/* <Link to="#" className="btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                      <i className="las la-expand-arrows-alt" />
                    </Link> */}

                    <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                      <Button className="custom-btn-drop d-inline-flex align-items-center" type="text">
                        <i className="la la-download custom-icon me-2" /> Download <DownOutlined className="ms-2" />
                      </Button>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <PrintReportPreview
            visible={pdfVisible}
            onClose={handleCloseModal}
            reportTitle="OTA Booking Volume Report"
            propertyName="Your Property Name"
            data={exportData}
            headers={exportHeaders}
            footerData={totalsForPageFooter}
            totalColumns={["Total Reservations", "Total Nights"]}
            onDownloadPDF={handleExportPDF}
            apiEndpoint="transactions/generate-pdf"
            apiUrl={process.env.REACT_APP_API_URL}
          />
      </div>
          <div className="row g-4 mb-4" style={{ padding: "15px 20px" }}>
            {activityStats.map((stat, index) => (
              <div
                key={index}
                className="col-md-2 col-6 stats-box"
                style={{ margin: "0 15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", padding: "10px", position: "relative" }}
              >
                <div
                  className="stats-line"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    backgroundColor: stat.color,
                    borderRadius: "4px 0 0 4px",
                  }}
                ></div>
                <div className="stats-content" style={{ paddingLeft: "10px" }}>
                  <h2 style={{ fontSize: "1.5rem", margin: 0, fontFamily: "'Roboto', sans-serif" }}>{stat.value}</h2>
                  <p style={{ margin: 0, color: "#666", fontFamily: "'Roboto', sans-serif" }}>{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          {showGraph && (
            <div className="row mb-4">
              <div className="col-md-12">
                <div
                  className="card"
                  style={{
                    borderRadius: "0",
                    overflowX: "auto",
                    border: "1px solid #dee2e6",
                    borderBottom: "none",
                  }}
                >
                  <div
                    className="card-header"
                    style={{
                      backgroundColor: "#fff",
                      padding: "12px 16px",
                      borderBottom: "none",
                      position: "relative",
                    }}
                  >
                    <h5
                      className="mb-0 d-flex align-items-center"
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#333",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {t("ota_booking_trends")}
                    </h5>
                    <div
                      style={{
                        position: "absolute",
                        bottom: "0",
                        left: "0",
                        width: "100%",
                        height: "4px",
                        backgroundColor: "#28a745",
                      }}
                    />
                  </div>
                  <div className="card-body p-0">
                    {loading ? (
                      <div className="text-center my-5">
                        <div className="loader">
                          <div className="dot"></div>
                          <div className="dot"></div>
                          <div className="dot"></div>
                          <div className="dot"></div>
                          <div className="dot"></div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ height: "300px", width: "100%" }}>
                      <Bar data={barChartData} options={barChartOptions} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="table-responsive" style={{ minHeight: "60vh", height: "calc(100vh - 150px)", overflow: "auto" }}>
            {loading ? (
              <div
                id="loader-wrapper-"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div className="loader">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            ) : data.length === 0 ? (
              <div
                className="no-data-container text-center mt-4"
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                  alt="No data available"
                  style={{ width: "150px", marginBottom: "20px" }}
                />
                <h3>No Data Available</h3>
                <p>No OTA bookings found for the selected date range.</p>
              </div>
            ) : (
              <Table
                columns={[
                  {
                    title: t("date"),
                    dataIndex: "check_in",
                    key: "check_in",
                    render: (text) => text ? renderDate(text) : "-",
                    sorter: (a, b) => (a.check_in ? moment(a.check_in).unix() : 0) - (b.check_in ? moment(b.check_in).unix() : 0),
                    width: 150,
                  },
                  {
                    title: t("booking_number"),
                    dataIndex: "booking_number",
                    key: "booking_number",
                    sorter: (a, b) => a.booking_number.localeCompare(b.booking_number),
                    width: 200,
                    render: (text) => text || "-",
                  },
                  {
                    title: t("ota_platform"),
                    dataIndex: "business_source_name",
                    key: "business_source_name",
                    sorter: (a, b) => (a.business_source_name || "").localeCompare(b.business_source_name || ""),
                    width: 150,
                    render: (text) => text || "-",
                  },
                  {
                    title: t("room_type"),
                    dataIndex: "room_type",
                    key: "room_type",
                    sorter: (a, b) => (a.room_type || "").localeCompare(b.room_type || ""),
                    width: 150,
                    render: (text) => text || "-",
                  },
                  {
                    title: t("status"),
                    dataIndex: "booking_status",
                    key: "booking_status",
                    render: (status) => (
                      <span
                        className={`colored-box status-box ${
                          status === "reserved"
                            ? "bg-primary"
                            : status === "checked_in"
                            ? "bg-success"
                            : status === "checked_out"
                            ? "bg-secondary"
                            : status === "cancelled"
                            ? "bg-danger"
                            : "bg-default"
                        }`}
                      >
                        {status.replace("_", " ").toUpperCase()}
                      </span>
                    ),
                    width: 150,
                  },
                  {
                    title: t("guest_name"),
                    dataIndex: "guest_name",
                    key: "guest_name",
                    sorter: (a, b) => (a.guest_name || "").localeCompare(b.guest_name || ""),
                    width: 200,
                    render: (text) => text || "-",
                  },
                  {
                    title: t("check_in"),
                    dataIndex: "check_in",
                    key: "check_in",
                    render: (text) => text ? renderDate(text) : "-",
                    sorter: (a, b) => (a.check_in ? moment(a.check_in).unix() : 0) - (b.check_in ? moment(b.check_in).unix() : 0),
                    width: 150,
                  },
                  {
                    title: t("check_out"),
                    dataIndex: "check_out",
                    key: "check_out",
                    render: (text) => text ? renderDate(text) : "-",
                    sorter: (a, b) => (a.check_out ? moment(a.check_out).unix() : 0) - (b.check_out ? moment(b.check_out).unix() : 0),
                    width: 150,
                  },
                ]}
                dataSource={balancedBookingData}
                pagination={{ pageSize: 20 }}
                className="table-striped custom-table"
                rowKey={(record) => record.key}
                scroll={{ x: 2000, y: "calc(100vh - 150px)" }}
              />
            )}
          </div>

          {/* Filter Modal */}
          <div
            className="modal custom-modal fade"
            id="scrollable-modal"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="filterModalTitle"
            aria-hidden="true"
          >
            <div
              className="modal-dialog modal-lg"
              style={{
                margin: 0,
                position: "fixed",
                right: 0,
                height: "100vh",
                width: "40%",
                zIndex: 1060,
              }}
              role="document"
            >
              <div className="modal-content h-100">
                <div className="modal-header">
                  <h5 className="modal-title" id="filterModalTitle">
                    Filter
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div
                  className="modal-body"
                  style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}
                >
                  <div className="row">
                    <div className="col-sm-12 col-md-12 mb-3">
                      <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                        <label className="form-label me-2" style={{ minWidth: "150px" }}>Status:</label>
                        <Select
                          options={bookingStatusOptions}
                          placeholder="Select Status"
                          value={bookingStatusFilter}
                          onChange={setBookingStatusFilter}
                          isClearable
                          styles={{
                            control: (base) => ({
                              ...base,
                              minWidth: "300px",
                              borderRadius: 4,
                              borderColor: "#d9d9d9",
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 1070,
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 1070,
                            }),
                          }}
                          menuPortalTarget={document.body}
                        />
                      </div>
                    </div>

                    <div className="col-sm-12 col-md-12 mb-3">
                      <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                        <label className="form-label me-2" style={{ minWidth: "150px" }}>Business Source:</label>
                        <Select
                          options={businessSourceOptions}
                          placeholder="Select Source"
                          value={businessSourceFilter}
                          onChange={handleBusinessSourceFilterChange}
                          isClearable
                          styles={{
                            control: (base) => ({
                              ...base,
                              minWidth: "300px",
                              borderRadius: 4,
                              borderColor: "#d9d9d9",
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 1070,
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 1070,
                            }),
                          }}
                          menuPortalTarget={document.body}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => {
                      setBookingStatusFilter(null);
                      setBusinessSourceFilter(null);
                    }}
                    data-bs-dismiss="modal"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {}}
                    data-bs-dismiss="modal"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

  );
};

export default OTABookingVolumeReport;